import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [particles, setParticles] = useState([]);
  const [shatterTiles, setShatterTiles] = useState([]);
  const [showShatter, setShowShatter] = useState(false);
  const [floatingParticles, setFloatingParticles] = useState([]);

  // Initialize floating particles on mount
  useEffect(() => {
    const particles = [...Array(20)].map((_, i) => ({
      id: i,
      initialX: Math.random() * window.innerWidth,
      initialY: Math.random() * window.innerHeight,
      animateY: Math.random() * -200 - 100,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
    }));
    setFloatingParticles(particles);
  }, []);

  // Simulate loading progress
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setLoadingComplete(true), 300);
          return 100;
        }
        // Randomized progress for realistic feel
        const increment = Math.random() * 15 + 5;
        return Math.min(prev + increment, 100);
      });
    }, 150);

    return () => clearInterval(interval);
  }, []);

  // Generate pixelate particles for the button
  const generateParticles = useCallback(() => {
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 200 - 100,
        y: Math.random() * 200 - 100,
        size: Math.random() * 8 + 4,
        delay: Math.random() * 0.3,
        duration: Math.random() * 0.5 + 0.5,
      });
    }
    setParticles(newParticles);
  }, []);

  // Generate shatter tiles for full screen effect
  const generateShatterTiles = useCallback(() => {
    const tiles = [];
    const cols = 8;
    const rows = 6;
    const tileWidth = window.innerWidth / cols;
    const tileHeight = window.innerHeight / rows;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const tileX = col * tileWidth;
        const tileY = row * tileHeight;
        
        // Calculate distance from center for staggered timing
        const tileCenterX = tileX + tileWidth / 2;
        const tileCenterY = tileY + tileHeight / 2;
        const distanceFromCenter = Math.sqrt(
          Math.pow(tileCenterX - centerX, 2) + Math.pow(tileCenterY - centerY, 2)
        );
        const maxDistance = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
        const normalizedDistance = distanceFromCenter / maxDistance;
        
        // Direction away from center
        const dirX = (tileCenterX - centerX) / centerX;
        const dirY = (tileCenterY - centerY) / centerY;
        
        tiles.push({
          id: `${row}-${col}`,
          x: tileX,
          y: tileY,
          width: tileWidth + 2,
          height: tileHeight + 2,
          targetX: dirX * 150,
          targetY: dirY * 150,
          rotation: (Math.random() - 0.5) * 30,
          delay: normalizedDistance * 0.15, // Ripple from center
          duration: 0.6,
        });
      }
    }
    setShatterTiles(tiles);
  }, []);

  const handleEnter = () => {
    generateParticles();
    setIsExiting(true);
    
    // Start shatter effect after button particles
    setTimeout(() => {
      generateShatterTiles();
      setShowShatter(true);
    }, 200);
    
    setTimeout(() => {
      onComplete();
    }, 1200);
  };

  return (
    <AnimatePresence>
      {!isExiting || particles.length > 0 ? (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          style={{
            background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0f0f2d 100%)',
          }}
        >
          {/* Animated background grid */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
                animation: 'gridMove 20s linear infinite',
              }}
            />
          </div>

          {/* Floating particles background */}
          <div className="absolute inset-0">
            {floatingParticles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute w-2 h-2 rounded-full bg-violet-500/30"
                initial={{
                  x: particle.initialX,
                  y: particle.initialY,
                }}
                animate={{
                  y: [null, particle.animateY],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: particle.duration,
                  repeat: Infinity,
                  delay: particle.delay,
                }}
              />
            ))}
          </div>

          {/* Glowing orbs */}
          <motion.div
            className="absolute w-96 h-96 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo/Title */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="mb-12"
            >
              <h1 
                className="text-6xl md:text-8xl font-black tracking-tighter"
                style={{
                  background: 'linear-gradient(135deg, #fff 0%, #a78bfa 50%, #7c3aed 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 80px rgba(139, 92, 246, 0.5)',
                }}
              >
                RAZIENT
              </h1>
              <motion.p
                className="text-center text-violet-300/80 text-lg tracking-[0.3em] mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                BOOKING
              </motion.p>
            </motion.div>

            {/* Loading bar container */}
            {!loadingComplete && (
              <motion.div
                className="w-80 md:w-96"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                {/* Progress percentage */}
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-violet-400 font-mono">INITIALIZING</span>
                  <span className="text-violet-300 font-mono">{Math.round(progress)}%</span>
                </div>

                {/* Outer container with glow */}
                <div 
                  className="relative h-3 rounded-full overflow-hidden"
                  style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    boxShadow: '0 0 20px rgba(139, 92, 246, 0.2), inset 0 0 10px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                  }}
                >
                  {/* Progress bar */}
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #7c3aed 0%, #a78bfa 50%, #c4b5fd 100%)',
                      boxShadow: '0 0 20px rgba(167, 139, 250, 0.8), 0 0 40px rgba(139, 92, 246, 0.4)',
                    }}
                    initial={{ width: '0%' }}
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: 'easeOut' }}
                  />

                  {/* Scanning line effect */}
                  <motion.div
                    className="absolute inset-y-0 w-20"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                    }}
                    animate={{
                      x: ['-80px', '400px'],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                </div>

                {/* Loading text */}
                <motion.p
                  className="text-center text-white/50 text-xs mt-3 font-mono tracking-wider"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  LOADING EXPERIENCE...
                </motion.p>
              </motion.div>
            )}

            {/* Enter button */}
            <AnimatePresence>
              {loadingComplete && !isExiting && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="relative"
                >
                  <motion.button
                    onClick={handleEnter}
                    className="relative px-12 py-4 text-lg font-bold tracking-wider overflow-hidden group"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.3) 100%)',
                      border: '2px solid rgba(167, 139, 250, 0.5)',
                      borderRadius: '4px',
                      color: '#fff',
                      textShadow: '0 0 10px rgba(167, 139, 250, 0.8)',
                      boxShadow: '0 0 30px rgba(139, 92, 246, 0.3), inset 0 0 20px rgba(139, 92, 246, 0.1)',
                    }}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: '0 0 50px rgba(139, 92, 246, 0.5), inset 0 0 30px rgba(139, 92, 246, 0.2)',
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Button glow effect */}
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                      }}
                      animate={{
                        x: ['-100%', '100%'],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                    />
                    
                    {/* Corner accents */}
                    <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-violet-400" />
                    <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-violet-400" />
                    <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-violet-400" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-violet-400" />

                    <span className="relative z-10">ENTER WEBSITE</span>
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pixelate particles when exiting */}
            <AnimatePresence>
              {isExiting && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {particles.map((particle) => (
                    <motion.div
                      key={particle.id}
                      className="absolute"
                      style={{
                        width: particle.size,
                        height: particle.size,
                        background: `linear-gradient(135deg, #a78bfa ${Math.random() * 50}%, #7c3aed 100%)`,
                        boxShadow: '0 0 10px rgba(167, 139, 250, 0.8)',
                      }}
                      initial={{ 
                        x: 0, 
                        y: 0, 
                        opacity: 1,
                        scale: 1,
                      }}
                      animate={{ 
                        x: particle.x * 3,
                        y: particle.y * 3,
                        opacity: 0,
                        scale: 0,
                        rotate: Math.random() * 360,
                      }}
                      transition={{
                        duration: particle.duration,
                        delay: particle.delay,
                        ease: 'easeOut',
                      }}
                    />
                  ))}
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom decorative elements */}
          <motion.div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full bg-violet-500/50"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </motion.div>

          {/* Scanline effect */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />

          {/* Full screen shatter effect */}
          <AnimatePresence>
            {showShatter && (
              <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
                {/* Smooth fade tiles that dissolve outward */}
                {shatterTiles.map((tile) => (
                  <motion.div
                    key={tile.id}
                    className="absolute"
                    style={{
                      left: tile.x,
                      top: tile.y,
                      width: tile.width,
                      height: tile.height,
                      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0f0f2d 100%)',
                    }}
                    initial={{ 
                      opacity: 1,
                      scale: 1,
                      x: 0,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    animate={{ 
                      opacity: 0,
                      scale: 1.1,
                      x: tile.targetX,
                      y: tile.targetY,
                      filter: 'blur(8px)',
                    }}
                    transition={{
                      duration: tile.duration,
                      delay: tile.delay,
                      ease: [0.4, 0, 0.2, 1], // Smooth easeInOut
                    }}
                  />
                ))}
                
                {/* Central white flash */}
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(167, 139, 250, 0.5) 30%, transparent 70%)',
                  }}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 2] }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />

                {/* Smooth ring expansion */}
                <motion.div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    width: '100px',
                    height: '100px',
                    border: '3px solid rgba(167, 139, 250, 0.8)',
                    boxShadow: '0 0 30px rgba(167, 139, 250, 0.6), inset 0 0 30px rgba(167, 139, 250, 0.3)',
                  }}
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: 20, opacity: 0 }}
                  transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                />

                {/* Second ring with delay */}
                <motion.div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    width: '80px',
                    height: '80px',
                    border: '2px solid rgba(139, 92, 246, 0.6)',
                    boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)',
                  }}
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: 25, opacity: 0 }}
                  transition={{ duration: 0.9, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
                />

                {/* Soft particle burst */}
                {[...Array(16)].map((_, i) => {
                  const angle = (i / 16) * Math.PI * 2;
                  const distance = 200 + Math.random() * 100;
                  return (
                    <motion.div
                      key={`particle-${i}`}
                      className="absolute left-1/2 top-1/2 rounded-full"
                      style={{
                        width: 6,
                        height: 6,
                        background: 'rgba(167, 139, 250, 0.9)',
                        boxShadow: '0 0 10px rgba(167, 139, 250, 0.8)',
                      }}
                      initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                      animate={{ 
                        x: Math.cos(angle) * distance,
                        y: Math.sin(angle) * distance,
                        opacity: 0,
                        scale: 0.5,
                      }}
                      transition={{
                        duration: 0.7,
                        delay: 0.05,
                        ease: [0.4, 0, 0.2, 1],
                      }}
                    />
                  );
                })}

                {/* Final fade overlay */}
                <motion.div
                  className="absolute inset-0 bg-transparent"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                />
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      ) : null}

      <style>{`
        @keyframes gridMove {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }
      `}</style>
    </AnimatePresence>
  );
}
